import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

// Add auth token to requests
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Duplicate-write protection for every mutating call.
// 1. An identical request that is already in flight reuses the same promise,
//    so rubber-clicking a button never sends the command twice.
// 2. Each logical submission carries an X-Idempotency-Key. The key is only
//    discarded once the request succeeds, so retrying after a timeout/failure
//    replays the original result on the server instead of writing again.
// ---------------------------------------------------------------------------
const newIdempotencyKey = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const fingerprintBody = (data) => {
  if (data === undefined || data === null) return 'null';
  if (typeof data === 'string') return data;
  if (typeof FormData !== 'undefined' && data instanceof FormData) return null;
  if (typeof Blob !== 'undefined' && data instanceof Blob) return null;
  try { return JSON.stringify(data); } catch { return null; }
};

const inFlightRequests = new Map();
const KEY_TTL_MS = 15 * 60 * 1000;
const KEY_STORE_NAME = 'ygw_idem_keys';

// Keys survive a page reload so that resubmitting an entry after a refresh
// (the app felt stuck, so the user reloaded) replays instead of writing twice.
const loadIdempotencyKeys = () => {
  try {
    const raw = sessionStorage.getItem(KEY_STORE_NAME);
    if (!raw) return new Map();
    const now = Date.now();
    return new Map(Object.entries(JSON.parse(raw)).filter(([, v]) => now - v.at <= KEY_TTL_MS));
  } catch { return new Map(); }
};
const idempotencyKeys = loadIdempotencyKeys();
const persistIdempotencyKeys = () => {
  try { sessionStorage.setItem(KEY_STORE_NAME, JSON.stringify(Object.fromEntries(idempotencyKeys))); } catch {}
};

// High-frequency heartbeats and file-streaming endpoints are not worth tracking
const IDEMPOTENCY_SKIP = ['/auth/session/ping', '/backup/export'];

const withDuplicateGuard = (method, raw) => (url, ...rest) => {
  const hasBody = method !== 'delete';
  const data = hasBody ? rest[0] : undefined;
  const config = hasBody ? rest[1] : rest[0];
  const bodyFp = fingerprintBody(data);
  const call = (cfg) => (hasBody ? raw(url, data, cfg) : raw(url, cfg));
  if (bodyFp === null || IDEMPOTENCY_SKIP.some(p => url.startsWith(p))) return call(config);

  const fp = `${method} ${url} ${JSON.stringify(config?.params ?? null)} ${bodyFp}`;
  const running = inFlightRequests.get(fp);
  if (running) return running;

  let entry = idempotencyKeys.get(fp);
  if (!entry || Date.now() - entry.at > KEY_TTL_MS) {
    entry = { key: newIdempotencyKey(), at: Date.now() };
    idempotencyKeys.set(fp, entry);
    persistIdempotencyKeys();
  }
  const cfg = { ...(config || {}), headers: { ...(config?.headers || {}), 'X-Idempotency-Key': entry.key } };
  const promise = call(cfg)
    .then(res => { idempotencyKeys.delete(fp); persistIdempotencyKeys(); return res; })
    .finally(() => { inFlightRequests.delete(fp); });
  inFlightRequests.set(fp, promise);
  return promise;
};

['post', 'put', 'patch', 'delete'].forEach(method => {
  const raw = API[method].bind(API);
  API[method] = withDuplicateGuard(method, raw);
});

export const formatWeight = (grams) => {
  if (grams === null || grams === undefined) return '—';
  return `${Number(grams).toFixed(3)}g`;
};

/** Get today's date in YYYY-MM-DD format using IST (Asia/Kolkata) - for internal/API use */
export const todayIST = () => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  const d = parts.find(p => p.type === 'day').value;
  return `${y}-${m}-${d}`;
};

/** Convert any date string to DD-MM-YYYY display format */
export const displayDate = (dateStr) => {
  if (!dateStr) return '—';
  // Handle YYYY-MM-DD
  const isoMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  // Handle DD-MM-YYYY (already correct)
  const dmyMatch = String(dateStr).match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmyMatch) return dateStr;
  return dateStr;
};

/** Convert DD-MM-YYYY to YYYY-MM-DD for API calls */
export const toAPIDate = (dateStr) => {
  if (!dateStr) return '';
  // Already YYYY-MM-DD
  const isoMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) return dateStr;
  // DD-MM-YYYY → YYYY-MM-DD
  const dmyMatch = String(dateStr).match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyMatch) return `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
  return dateStr;
};

/** Get today in DD-MM-YYYY display format */
export const todayDisplay = () => displayDate(todayIST());

export const formatDate = (isoString) => {
  if (!isoString) return '—';
  // If it's a plain date string (YYYY-MM-DD), convert directly
  const isoMatch = String(isoString).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  // Otherwise parse as datetime and format
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return String(isoString);
  const day = String(d.getDate()).padStart(2, '0');
  const mon = String(d.getMonth() + 1).padStart(2, '0');
  const yr = d.getFullYear();
  return `${day}-${mon}-${yr}`;
};

export const formatDateTime = (isoString) => {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return String(isoString);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  }).replace(/\//g, '-');
};

export const getPurityClass = (purity) => {
  return `purity-${purity}`;
};

/** Natural sort comparator for box IDs like C-1, C-2, C-10 */
export function naturalBoxSort(a, b) {
  const re = /(\D+)(\d+)/;
  const ma = (a || '').match(re);
  const mb = (b || '').match(re);
  if (ma && mb) {
    const prefixCmp = ma[1].localeCompare(mb[1]);
    if (prefixCmp !== 0) return prefixCmp;
    return parseInt(ma[2], 10) - parseInt(mb[2], 10);
  }
  return (a || '').localeCompare(b || '');
}

/** Sort an array of box objects by box_id naturally */
export function sortBoxesNatural(boxes) {
  return [...boxes].sort((a, b) => naturalBoxSort(a.box_id, b.box_id));
}
